import { useState, useEffect, useMemo, useCallback } from 'react';
import { Form } from '../../../../components';
import { handleError } from '../../../../lib/helpers';
import { useNotificationContext } from '../../../../providers';
import { useCloudAttributes } from '../hooks/useCloudAttributes';
import { useApplianceRedeployPrefill } from './useApplianceRedeployPrefill';
import { buildSourceCloudsPayload } from '../components/VMBasedMigrationsConfig/helpers';
import { useApplianceCommonState } from '../components/CommonApplianceConfiguration/useApplianceCommonState';
import { IP_TYPE, NIC_TYPE_ETHERNET } from '../../../../constants/network';

const DEFAULT_INSTANCE_TYPE = 't3.large';

const SHARED_TENANCY_INSTANCE_TYPES = [{ value: DEFAULT_INSTANCE_TYPE, label: DEFAULT_INSTANCE_TYPE }];

const PREFERRED_OUTPOST_INSTANCE_TYPES = ['c5.large', 'm5.large'];

export const getOutpostInstanceTypes = (subnetId, subnets = [], outposts = []) => {
    const selectedSubnet = subnets.find((_subnet) => (_subnet.id || _subnet.name) === subnetId);
    const outpost = outposts.find((_outpost) => _outpost.arn === selectedSubnet?.outpost_arn);
    if (outpost?.instance_types?.length) {
        return outpost.instance_types;
    } else {
        return null;
    }
};

export const getOutpostInstanceType = (instanceTypes, currentInstanceType) => {
    if (instanceTypes.includes(currentInstanceType)) {
        return currentInstanceType;
    } else {
        return PREFERRED_OUTPOST_INSTANCE_TYPES.find((type) => instanceTypes.includes(type)) || instanceTypes[0];
    }
};

const buildAWSApplianceNetworkPayload = (values) => {
    const payload = {
        interfaces: {
            eth0: {
                ip_type: IP_TYPE.DHCP,
                network_name: values.subnet,
                assign_public_ip: values.assignPublicIp === 'enable',
                ip_addr: values.staticPrivateIp || '',
                type: NIC_TYPE_ETHERNET,
                dv_switch_name: '',
            },
        },
    };
    if (values.staticPrivateIp) {
        payload.assign_private_ip = true;
    }
    return payload;
};

export const useAWSApplianceSetup = ({ cloudAccount, onSubmit, isRedeploy }) => {
    const [form] = Form.useForm();
    const { notifyError } = useNotificationContext();

    const [regions, setRegions] = useState([]);
    const [vpcs, setVPCs] = useState([]);
    const [subnets, setSubnets] = useState([]);
    const [kmsKeys, setKmsKeys] = useState([]);
    const [dedicatedHosts, setDedicatedHosts] = useState([]);
    const [fsxFilesystems, setFsxFilesystems] = useState([]);
    const [outposts, setOutposts] = useState([]);
    const [instanceTags, setInstanceTags] = useState({});
    const [loadingRegionData, setLoadingRegionData] = useState(false);

    const region = Form.useWatch('region', form);
    const vpc = Form.useWatch('vpc', form);
    const subnet = Form.useWatch('subnet', form);
    const tenancy = Form.useWatch('tenancy', form);
    const hostId = Form.useWatch('hostId', form);
    const instanceType = Form.useWatch('instanceType', form);
    const encryptionEnabled = Form.useWatch('encryptionEnabled', form);
    const encryptionType = Form.useWatch('encryptionType', form);
    const enableFsx = Form.useWatch('enableFsx', form);

    const {
        useProxyEnabled,
        migrationInstructions,
        ignoreValidationErrors,
        commonConfigProps,
        prefillCommonFromAppliance,
    } = useApplianceCommonState(form);

    const handleAttributesLoaded = (result) => {
        if (result?.properties?.regions) {
            setRegions(result.properties.regions);
        }
    };

    const { initialLoading, loadingError, loadCloudAttributes, fetchAttributes } = useCloudAttributes(
        cloudAccount,
        handleAttributesLoaded,
    );

    useApplianceRedeployPrefill({
        isRedeploy,
        cloudAccount,
        regions,
        form,
        fetchAttributes,
        prefillCommonFromAppliance,
        setVPCs,
        setSubnets,
        setKmsKeys,
        setDedicatedHosts,
        setFsxFilesystems,
        setOutposts,
        setInstanceTags,
        setLoadingRegionData,
    });

    useEffect(() => {
        if (isRedeploy) {
            return;
        }
        const shouldAddDefaultTags = ['auto', 'staging', 'ops'].some(env => {
            return window.location.href.includes(env)
        });
        const flagSet = (sessionStorage.getItem('addAWSApplianceTags') || 'false').toLowerCase() === 'true';
        if (shouldAddDefaultTags || flagSet) {
            setInstanceTags({
                'rm:runDuration': '12',
                'rm:tenant': 'migrate',
                'rm:taggingVersion': 'v2.1',
                'rm:createdby': sessionStorage.getItem('userEmail') || '',
                'rm:createdbyteam': 'qe',
                'rm:ownerTeam': 'qe',
                'rm:lockTags': 'false',
            });
        }
    }, [isRedeploy]);

    const availableDedicatedHosts = useMemo(
        () => (dedicatedHosts || []).filter((host) => host.state === 'available'),
        [dedicatedHosts],
    );

    const handleRegionChange = useCallback(
        async (regionName) => {
            setVPCs([]);
            setSubnets([]);
            setKmsKeys([]);
            setDedicatedHosts([]);
            setFsxFilesystems([]);
            setOutposts([]);
            form.setFieldsValue({
                vpc: undefined,
                subnet: undefined,
                assignPublicIp: 'disable',
                staticPrivateIp: '',
                tenancy: 'default',
                hostId: 'default',
                affinity: 'host',
                instanceType: DEFAULT_INSTANCE_TYPE,
                enableFsx: false,
                fsxFilesystemId: undefined,
                fsxUsername: '',
                fsxPassword: '',
            });

            setLoadingRegionData(true);
            try {
                const result = await fetchAttributes({
                    cloudAccountId: cloudAccount.id,
                    params: { region: regionName },
                }).unwrap();

                const regionData = result?.properties?.regions?.[0];
                if (regionData) {
                    setVPCs(regionData.vpcs || []);
                    setKmsKeys(regionData.kms_keys || []);
                    setDedicatedHosts(regionData.hosts || []);
                    setFsxFilesystems(regionData.fsx_filesystems || []);
                    setOutposts(regionData.outposts || []);
                }
            } catch (error) {
                handleError(error, notifyError, 'Failed to load region attributes');
            } finally {
                setLoadingRegionData(false);
            }
        },
        [cloudAccount, fetchAttributes, form, notifyError],
    );

    const handleVPCChange = useCallback(
        (vpcId) => {
            const selectedVpc = vpcs.find((vpc) => vpc.id === vpcId);
            setSubnets(selectedVpc?.subnets || []);
            form.setFieldsValue({
                subnet: undefined,
                assignPublicIp: 'disable',
                staticPrivateIp: '',
                instanceType: DEFAULT_INSTANCE_TYPE,
            });
        },
        [vpcs, form],
    );

    const handleEncryptionTypeChange = useCallback(() => {
        form.setFieldsValue({ kmsKey: undefined });
    }, [form]);

    const handleTenancyChange = useCallback(
        (value) => {
            const fields = { hostId: 'default', affinity: 'host' };
            if (value !== 'host') {
                fields.instanceType = DEFAULT_INSTANCE_TYPE;
            }
            form.setFieldsValue(fields);
        },
        [form],
    );

    const handleHostChange = useCallback(
        (value) => {
            if (!value || value === 'default') {
                return;
            }
            const host = availableDedicatedHosts.find((host) => host.id === value);
            const hostInstanceType = host?.instance_capacity?.[0]?.instance_type;
            if (hostInstanceType) {
                form.setFieldsValue({ instanceType: hostInstanceType });
            }
        },
        [availableDedicatedHosts, form],
    );

    const outpostInstanceTypes = useMemo(
        () => getOutpostInstanceTypes(subnet, subnets, outposts),
        [subnet, subnets, outposts],
    );

    const handleSubnetChange = useCallback(
        (value) => {
            const instanceTypes = getOutpostInstanceTypes(value, subnets, outposts);
            if (instanceTypes) {
                form.setFieldsValue({
                    instanceType: getOutpostInstanceType(instanceTypes, form.getFieldValue('instanceType')),
                });
            } else if (form.getFieldValue('tenancy') !== 'host') {
                form.setFieldsValue({ instanceType: DEFAULT_INSTANCE_TYPE });
            }
        },
        [subnets, outposts, form],
    );

    const instanceTypeOptions = useMemo(() => {
        let options;
        if (outpostInstanceTypes) {
            options = outpostInstanceTypes.map((instance_type) => ({
                value: instance_type,
                label: instance_type,
            }));
        } else if (tenancy !== 'host') {
            options = SHARED_TENANCY_INSTANCE_TYPES;
        } else if (hostId && hostId !== 'default') {
            const host = availableDedicatedHosts.find((host) => host.id === hostId);
            const hostInstanceType = host?.instance_capacity?.[0]?.instance_type;
            options = hostInstanceType ? [{ value: hostInstanceType, label: hostInstanceType }] : [];
        } else {
            const instance_types = new Set(
                availableDedicatedHosts.map((host) => host?.instance_capacity?.[0]?.instance_type).filter(Boolean),
            );
            options = Array.from(instance_types).map((instance_type) => ({
                value: instance_type,
                label: instance_type,
            }));
        }

        if (instanceType && !options.some((option) => option.value === instanceType)) {
            return [{ value: instanceType, label: instanceType }, ...options];
        }
        return options;
    }, [outpostInstanceTypes, tenancy, hostId, availableDedicatedHosts, instanceType]);

    const regionOptions = useMemo(
        () =>
            [...regions]
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .map((item) => ({
                value: item.name,
                label: item.label ? `${item.label} | ${item.name}` : item.name,
            })),
        [regions],
    );

    const hostOptions = useMemo(
        () => [
            { value: 'default', label: 'Auto-placement' },
            ...availableDedicatedHosts.map((host) => ({
                value: host.id,
                label: `${host.id} | ${host.availability_zone}`,
            })),
        ],
        [availableDedicatedHosts],
    );

    const kmsKeyOptions = useMemo(
        () =>
            kmsKeys
            .filter((kmsKey) => {
                if (encryptionType === 'platform_key') {
                    return kmsKey.key_manager === 'AWS';
                }
                if (encryptionType === 'customer_key') {
                    return kmsKey.key_manager === 'CUSTOMER';
                }
                return true;
            })
            .map((kmsKey) => ({
                value: kmsKey.arn,
                label: kmsKey.description || kmsKey.arn,
            })),
        [kmsKeys, encryptionType],
    );

    const fsxFilesystemOptions = useMemo(
        () =>
            fsxFilesystems.map((filesystem) => ({
                value: filesystem.id,
                label: `${filesystem.name} (${filesystem.id})`,
            })),
        [fsxFilesystems],
    );

    const vpcOptions = useMemo(
        () =>
            vpcs.map((item) => ({
                value: item.id,
                label: `${item.id} (${item.cidr_block})${item.name ? ` | ${item.name}` : ''}${
                    item.is_default ? ' (default)' : ''
                }`,
            })),
        [vpcs],
    );

    const handleFormSubmit = useCallback(
        (values) => {
            const selectedSubnet = subnets.find((subnet) => subnet.id === values.subnet);
            const isDedicatedHost = values.tenancy === 'host';

            const cloudProperties = {
                region: values.region,
                vpc: values.vpc,
                az: selectedSubnet?.availability_zone,
                vapp_name: values.instanceName || undefined,
                instance_type: values.instanceType,
                tenancy: isDedicatedHost ? 'host' : null,
                host_id: isDedicatedHost ? values.hostId : null,
                affinity: isDedicatedHost ? values.affinity || 'host' : null,
                encrypt_volumes: !!values.encryptionEnabled,
                encryption_key_arn:
                    values.encryptionEnabled && values.encryptionType !== 'default_key' ? values.kmsKey : null,
                network: buildAWSApplianceNetworkPayload(values),
                tags: instanceTags || {},
                proxy_type: 'none',
            };

            if (values.enableFsx && values.fsxFilesystemId) {
                const filesystem = fsxFilesystems.find((candidate) => candidate.id === values.fsxFilesystemId);
                cloudProperties.aws_fsx = [
                    {
                        id: values.fsxFilesystemId,
                        management_ip: filesystem?.management_ip,
                        credentials: {
                            username: values.fsxUsername,
                            password: values.fsxPassword,
                        },
                    },
                ];
            }

            const payload = {
                cloud_properties: cloudProperties,
                vm_level_migration_enabled: !!values.vmLevelEnabled,
                ignore_validation_errors: ignoreValidationErrors,
                ca_proxy: useProxyEnabled,
                migration_instructions: migrationInstructions,
            };

            if (values.vmLevelEnabled) {
                payload.source_clouds = buildSourceCloudsPayload(values);
            }

            onSubmit(payload);
        },
        [
            form,
            subnets,
            instanceTags,
            fsxFilesystems,
            ignoreValidationErrors,
            useProxyEnabled,
            migrationInstructions,
            onSubmit,
        ],
    );

    return {
        form,
        initialLoading,
        loadingError,
        loadCloudAttributes,
        loadingRegionData,
        region,
        vpc,
        tenancy,
        hostId,
        encryptionEnabled,
        encryptionType,
        enableFsx,
        subnets,
        availableDedicatedHosts,
        instanceTags,
        setInstanceTags,
        handleRegionChange,
        handleVPCChange,
        handleSubnetChange,
        handleEncryptionTypeChange,
        handleTenancyChange,
        handleHostChange,
        handleFormSubmit,
        regionOptions,
        hostOptions,
        kmsKeyOptions,
        fsxFilesystemOptions,
        vpcOptions,
        instanceTypeOptions,
        commonConfigProps,
    };
};

